/**
 * Family Tree Mutation Validation - Type Definitions
 *
 * This module defines all types and interfaces for the validation system.
 * Used to protect the family graph from invalid mutations before they're sent to the backend.
 */

import type { PersonNodeCanvas, RelationshipEdge } from '@/types/tree-dashboard.types';

// ==================== VALIDATION RESULT TYPES ====================

/**
 * Validation severity levels
 */
export type ValidationSeverity = 'error' | 'warning';

/**
 * Validation error codes for structured error handling
 */
export type ValidationErrorCode =
  | 'SELF_RELATIONSHIP'
  | 'DUPLICATE_SPOUSE'
  | 'DUPLICATE_PARENT_CHILD'
  | 'INVALID_PARENT_COUNT'
  | 'ANCESTRY_CYCLE'
  | 'INVALID_RELATIONSHIP_DIRECTION'
  | 'INCOMPATIBLE_MUTATION'
  | 'PERSON_NOT_FOUND'
  | 'NO_PARENTS_FOR_SIBLING'
  | 'UNKNOWN_ERROR';

/**
 * Individual validation violation
 */
export interface ValidationViolation {
  code: ValidationErrorCode;
  severity: ValidationSeverity;
  message: string;
  details?: Record<string, unknown>;
}

/**
 * Complete validation result
 */
export interface ValidationResult {
  valid: boolean;
  violations: ValidationViolation[];
  metadata?: {
    rulesPassed: string[];
    rulesChecked: number;
    validationTimestamp: number;
  };
}

// ==================== MUTATION TYPES ====================

/**
 * Mutation type - what kind of relationship is being added
 */
export type MutationType = 'add_spouse' | 'add_parent' | 'add_child' | 'add_sibling';

/**
 * Base mutation interface
 */
export interface BaseMutation {
  type: MutationType;
  sourcePersonId: string; // The person initiating the relationship
  targetPersonId: string; // The new person being added (or existing person being linked)
}

/**
 * Add spouse mutation
 */
export interface AddSpouseMutation extends BaseMutation {
  type: 'add_spouse';
}

/**
 * Add parent mutation
 */
export interface AddParentMutation extends BaseMutation {
  type: 'add_parent';
  parentGender?: 'male' | 'female' | 'other'; // Optional gender constraint
}

/**
 * Add child mutation
 */
export interface AddChildMutation extends BaseMutation {
  type: 'add_child';
  otherParentId?: string; // Optional - if child should be linked to a couple
}

/**
 * Add sibling mutation
 */
export interface AddSiblingMutation extends BaseMutation {
  type: 'add_sibling';
  siblingGender: 'male' | 'female'; // Enforced from relationship type (brother/sister)
  parentIds?: string[]; // Inherited parent IDs from source person
}

/**
 * Union type for all mutations
 */
export type FamilyTreeMutation = AddSpouseMutation | AddParentMutation | AddChildMutation | AddSiblingMutation;

// ==================== GRAPH CONTEXT ====================

/**
 * Family tree graph context for validation
 * Contains all data needed to validate mutations
 */
export interface FamilyTreeGraphContext {
  persons: PersonNodeCanvas[];
  relationships: RelationshipEdge[];
}

// ==================== VALIDATION CONFIGURATION ====================

/**
 * Product rules - configurable limits for the family tree
 */
export interface ProductRules {
  maxParentsPerPerson: number; // Maximum biological parents (default: 2)
  allowMultipleSpouses: boolean; // Allow multiple spouse relationships (default: true)
  allowSelfRelationships: boolean; // Allow person to be linked to themselves (default: false)
}

/**
 * Default product rules for the application
 */
export const DEFAULT_PRODUCT_RULES: ProductRules = {
  maxParentsPerPerson: 2,
  allowMultipleSpouses: true,
  allowSelfRelationships: false,
};

// ==================== HELPER TYPES ====================

/**
 * Person relationship summary
 * Aggregated view of all relationships for a person
 */
export interface PersonRelationshipSummary {
  personId: string;
  parents: string[]; // Parent IDs
  children: string[]; // Child IDs
  spouses: string[]; // Spouse IDs
  siblings: string[]; // Sibling IDs (computed from shared parents)
}
